/**
 * Drones store: presets (from @tonist/presets) + the user's own drones.
 * Anonymous: user drones live in localStorage `tonist.drones`. Signed in: synced with /api/drones,
 * local drones migrated on first sign-in.
 */
import { create } from 'zustand';
import { DroneRecordDto, DroneSchema, parseDrone, type Drone, type DroneRecord } from '@tonist/shared';
import { PRESETS } from '@tonist/presets';
import { get, post, put, del } from '../lib/api';
import { safeStorage } from './settingsStore';

export const DRONES_KEY = 'tonist.drones';

export const presetById = (id: string): Drone | undefined => PRESETS.find((p) => p.id === id);

export const FALLBACK_DRONE: Drone = parseDrone({
  id: 'sa', name: 'Sa', description: 'A tanpura on Sa. The ground.', family: 'white',
  sources: [{ kind: 'sample', sampleId: 'tanpura-sa', rootMidi: 49 }],
  layers: { unison: { voices: 2, detuneCents: 3, spread: 0.5, phaseScatter: 1 } },
  filter: { type: 'comb', cutoffHz: 4000, q: 0.8, keyTrack: 1, combFeedback: 0.3 },
  breath: { rateHz: 0.07, depth: 0.08, shape: 'sine', phasePerVoice: true },
  space: { reverb: { size: 0.6, decaySec: 3.5, damping: 0.5, preDelayMs: 10, mix: 0.18 } },
  gesture: { velocityTo: 'none', radiusTo: 'jivari', angleBendCents: 45 },
});

export const newId = (): string => (typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : `d-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`);

const now = () => Date.now();

export function localRecord(drone: Drone, base?: Partial<DroneRecord>): DroneRecord {
  return {
    id: drone.id, userId: null, drone, isPublic: false, slug: null, plays: 0, forkedFrom: null, authorName: null,
    createdAt: now(), updatedAt: now(), ...base,
  };
}

export function loadLocalDrones(storage: Pick<Storage, 'getItem'> | null = safeStorage()): DroneRecord[] {
  try {
    const raw = storage?.getItem(DRONES_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    if (!Array.isArray(arr)) return [];
    return arr.map((r) => DroneRecordDto.safeParse(r)).filter((p) => p.success).map((p) => p.data);
  } catch { return []; }
}

export function saveLocalDrones(list: DroneRecord[], storage: Pick<Storage, 'setItem'> | null = safeStorage()): void {
  try { storage?.setItem(DRONES_KEY, JSON.stringify(list)); } catch { /* ignore */ }
}

const unwrapList = (data: unknown): DroneRecord[] => {
  const arr = Array.isArray(data) ? data : (data as { items?: unknown })?.items;
  if (!Array.isArray(arr)) return [];
  return arr.map((r) => DroneRecordDto.safeParse(r)).filter((p) => p.success).map((p) => p.data);
};
const unwrapOne = (data: unknown): DroneRecord => {
  const o = (data as { item?: unknown; drone?: unknown })?.item ?? data;
  const p = DroneRecordDto.safeParse(o);
  if (p.success) return p.data;
  // server might return the drone json directly
  return localRecord(parseDrone(o));
};

export interface DronesState {
  presets: Drone[];
  mine: DroneRecord[];
  /** drones loaded from public pages this session (playable, not owned) */
  transient: Record<string, DroneRecord>;
  signedIn: boolean;
  loading: boolean;
  error: string | null;
  /** Called on auth change. Signed in: fetch from server and migrate local drones. */
  sync(signedIn: boolean): Promise<void>;
  byId(id: string): Drone | undefined;
  recordById(id: string): DroneRecord | undefined;
  create(drone: Drone): Promise<DroneRecord>;
  update(id: string, drone: Drone): Promise<void>;
  remove(id: string): Promise<void>;
  duplicate(id: string): Promise<DroneRecord>;
  publish(id: string, on: boolean): Promise<DroneRecord>;
  clone(record: DroneRecord): Promise<DroneRecord>;
  stashTransient(record: DroneRecord): void;
}

/** Create an editable copy of a drone with a fresh id and " copy" suffix. */
export function copyOf(drone: Drone, name = `${drone.name} copy`): Drone {
  return DroneSchema.parse({ ...structuredClone(drone), id: newId(), name: name.slice(0, 60) });
}

export const useDrones = create<DronesState>((set, getState) => ({
  presets: PRESETS.length ? PRESETS : [FALLBACK_DRONE],
  mine: loadLocalDrones(),
  transient: {},
  signedIn: false,
  loading: false,
  error: null,

  async sync(signedIn) {
    if (!signedIn) { set({ signedIn: false, mine: loadLocalDrones() }); return; }
    set({ signedIn: true, loading: true, error: null });
    try {
      let remote = unwrapList(await get<unknown>('/drones'));
      // migrate local drones on first sign-in
      const local = loadLocalDrones();
      if (local.length) {
        const known = new Set(remote.map((r) => r.id));
        for (const rec of local) {
          if (known.has(rec.id)) continue;
          try { remote = [...remote, unwrapOne(await post<unknown>('/drones', { drone: rec.drone }))]; } catch { /* keep going */ }
        }
        saveLocalDrones([]);
      }
      set({ mine: remote, loading: false });
    } catch (e) {
      set({ loading: false, error: (e as Error).message, mine: loadLocalDrones() });
    }
  },

  byId(id) { const s = getState(); return s.presets.find((p) => p.id === id) ?? s.mine.find((m) => m.id === id)?.drone ?? s.transient[id]?.drone; },
  recordById(id) { const s = getState(); return s.mine.find((m) => m.id === id) ?? s.transient[id]; },

  async create(drone) {
    const s = getState();
    if (s.signedIn) {
      const rec = unwrapOne(await post<unknown>('/drones', { drone }));
      set({ mine: [rec, ...s.mine] });
      return rec;
    }
    const rec = localRecord(drone);
    const mine = [rec, ...s.mine];
    saveLocalDrones(mine);
    set({ mine });
    return rec;
  },

  async update(id, drone) {
    const s = getState();
    const mine = s.mine.map((r) => (r.id === id ? { ...r, drone, updatedAt: now() } : r));
    set({ mine });
    if (s.signedIn) await put(`/drones/${encodeURIComponent(id)}`, { drone });
    else saveLocalDrones(mine);
  },

  async remove(id) {
    const s = getState();
    const mine = s.mine.filter((r) => r.id !== id);
    set({ mine });
    if (s.signedIn) await del(`/drones/${encodeURIComponent(id)}`);
    else saveLocalDrones(mine);
  },

  async duplicate(id) {
    const s = getState();
    const src = s.byId(id);
    if (!src) throw new Error('No such drone');
    const isMine = s.mine.some((r) => r.id === id);
    if (s.signedIn && isMine) {
      const rec = unwrapOne(await post<unknown>(`/drones/${encodeURIComponent(id)}/duplicate`));
      set({ mine: [rec, ...getState().mine] });
      return rec;
    }
    return s.create(copyOf(src));
  },

  async publish(id, on) {
    const rec = unwrapOne(await post<unknown>(`/drones/${encodeURIComponent(id)}/${on ? 'publish' : 'unpublish'}`));
    set({ mine: getState().mine.map((r) => (r.id === id ? rec : r)) });
    return rec;
  },

  async clone(record) {
    const s = getState();
    if (s.signedIn) {
      const rec = unwrapOne(await post<unknown>(`/drones/${encodeURIComponent(record.id)}/clone`));
      set({ mine: [rec, ...s.mine] });
      return rec;
    }
    return s.create(copyOf(record.drone, record.drone.name));
  },

  stashTransient(record) { set({ transient: { ...getState().transient, [record.id]: record } }); },
}));

export const familyOfDrone = (id: string): Drone['family'] => useDrones.getState().byId(id)?.family ?? 'white';
